import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MousePointerClick, Store, Package, TrendingUp } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminClickAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/api/admin/click-analytics?top=20`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="click-analytics-loading">
        <div className="text-neutral-400">Loading click analytics…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white p-8" data-testid="click-analytics-error">
        <div className="max-w-3xl mx-auto rounded-xl border border-red-500/30 bg-red-500/5 p-6">
          <p className="text-red-300 font-semibold">Failed to load click analytics</p>
          <p className="text-red-300/70 text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  const { summary = {}, top_storefronts = [], top_products = [] } = data || {};

  return (
    <AdminLayout>
    <div className="min-h-screen bg-black text-white" data-testid="admin-click-analytics">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 rounded-full hover:bg-white/5 text-neutral-400"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-black tracking-tight">Click Analytics</h1>
            <p className="text-neutral-400 text-sm mt-1">
              What your visitors are actually clicking on — storefronts and products
            </p>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <SummaryTile
            icon={<Store className="w-5 h-5 text-emerald-300" />}
            label="Storefront clicks (7d)"
            value={summary.storefront_clicks_7d || 0}
            testId="summary-storefront-7d"
          />
          <SummaryTile
            icon={<Package className="w-5 h-5 text-cyan-300" />}
            label="Product clicks (7d)"
            value={summary.product_clicks_7d || 0}
            testId="summary-product-7d"
          />
          <SummaryTile
            icon={<TrendingUp className="w-5 h-5 text-amber-300" />}
            label="Total tracked clicks (7d)"
            value={(summary.storefront_clicks_7d || 0) + (summary.product_clicks_7d || 0)}
            testId="summary-total-7d"
          />
        </div>

        {/* Top storefronts */}
        <Section
          title="Top creator storefronts"
          icon={<Store className="w-5 h-5" />}
          rows={top_storefronts}
          emptyText="No storefront clicks yet. Start sending traffic and they'll show up here."
          renderRow={(c) => (
            <Row
              key={c.creator_id}
              testId={`storefront-row-${c.store_slug}`}
              avatar={c.avatar}
              name={c.store_name || '(no name)'}
              subtitle={c.store_slug ? `/${c.store_slug}` : ''}
              clicks7d={c.clicks_7d || 0}
              clicks30d={c.clicks_30d || 0}
              clicksAll={c.clicks_all || 0}
              onClick={() => navigate(`/store/${c.store_slug}`)}
            />
          )}
        />

        {/* Top products */}
        <Section
          title="Top products"
          icon={<Package className="w-5 h-5" />}
          rows={top_products}
          emptyText="No product clicks yet. Once visitors start clicking product cards, you'll see them ranked here."
          renderRow={(p) => (
            <Row
              key={p.product_id}
              testId={`product-row-${p.product_id}`}
              avatar={p.thumbnail_url}
              name={p.name || '(unnamed product)'}
              subtitle={`${p.is_house_product ? 'House · ' : ''}$${Number(p.price || 0).toFixed(2)}`}
              clicks7d={p.clicks_7d || 0}
              clicks30d={p.clicks_30d || 0}
              clicksAll={p.clicks_all || 0}
              onClick={() => navigate(`/product/${p.product_id}`)}
            />
          )}
        />
      </div>
    </div>
    </AdminLayout>
  );
}

const SummaryTile = ({ icon, label, value, testId }) => (
  <div
    className="rounded-xl border border-white/10 bg-neutral-900/40 p-5"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 text-neutral-400 text-xs uppercase tracking-wider">
      {icon}
      {label}
    </div>
    <div className="mt-3 text-3xl font-black">{value.toLocaleString()}</div>
  </div>
);

const Section = ({ title, icon, rows, emptyText, renderRow }) => (
  <section className="mb-12">
    <h2 className="flex items-center gap-2 text-lg font-bold mb-4">
      {icon}
      {title}
    </h2>
    {rows.length === 0 ? (
      <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-neutral-500 text-sm">
        {emptyText}
      </div>
    ) : (
      <div className="rounded-xl border border-white/10 bg-neutral-900/40 overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-wider text-neutral-500 border-b border-white/5">
          <div className="col-span-6">Name</div>
          <div className="col-span-2 text-right">7d</div>
          <div className="col-span-2 text-right">30d</div>
          <div className="col-span-2 text-right">All time</div>
        </div>
        {rows.map(renderRow)}
      </div>
    )}
  </section>
);

const Row = ({ testId, avatar, name, subtitle, clicks7d, clicks30d, clicksAll, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full grid grid-cols-12 items-center px-5 py-4 hover:bg-white/5 border-b border-white/5 last:border-0 text-left transition-colors"
    data-testid={testId}
  >
    <div className="col-span-6 flex items-center gap-3 min-w-0">
      {avatar ? (
        <img src={avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-neutral-800 flex items-center justify-center flex-shrink-0">
          <MousePointerClick className="w-4 h-4 text-neutral-500" />
        </div>
      )}
      <div className="min-w-0">
        <div className="font-semibold truncate">{name}</div>
        <div className="text-xs text-neutral-500 truncate">{subtitle}</div>
      </div>
    </div>
    <div className="col-span-2 text-right font-semibold">{clicks7d.toLocaleString()}</div>
    <div className="col-span-2 text-right text-neutral-300">{clicks30d.toLocaleString()}</div>
    <div className="col-span-2 text-right text-neutral-500">{clicksAll.toLocaleString()}</div>
  </button>
);
